import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import Navbar from "../components/storyforge/Navbar";
import { api, getUser } from "../lib/api";
import { SF } from "../constants/testIds";
import { toast } from "sonner";
import { validatePhotoFile, validateStoryForm } from "./create/storyFormValidation";
import { ShippingAddressFields } from "./create/ShippingAddressFields";
import { DedicationSection } from "./create/DedicationSection";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const STYLES = [
    "Soft watercolor",
    "Pencil sketch with warm color",
    "Modern flat illustration",
    "Studio Ghibli-inspired",
    "Vintage 1960s storybook",
    "Chalk pastel dreamscape",
];

export default function CreateStory() {
    useDocumentMeta({
        title: "Create a storybook — Athena's Atlas",
        description: "Tell us your child's name, the theme, lesson, and illustration style. We'll write a 24-page hardcover starring them, printed and shipped in 7–10 days.",
    });
    const navigate = useNavigate();
    const user = getUser();
    const [childName, setChildName] = useState("");
    const [bookTitle, setBookTitle] = useState("");
    const [theme, setTheme] = useState("");
    const [lesson, setLesson] = useState("");
    const [style, setStyle] = useState(STYLES[0]);
    const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
    const [submitting, setSubmitting] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoBase64, setPhotoBase64] = useState(null);
    const [photoMime, setPhotoMime] = useState(null);
    const [attireMode, setAttireMode] = useState("generated");
    const [dedicationChoice, setDedicationChoice] = useState(null);
    const [dedicationText, setDedicationText] = useState("");
    const fileRef = useRef(null);

    const onPhotoChange = (e) => {
        const file = e.target.files?.[0];
        const err = validatePhotoFile(file);
        if (err) {
            if (file) toast.error(err);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || "");
            const comma = result.indexOf(",");
            setPhotoPreview(result);
            setPhotoBase64(result.slice(comma + 1));
            setPhotoMime(file.type || "image/jpeg");
        };
        reader.readAsDataURL(file);
    };

    const clearPhoto = () => {
        setPhotoPreview(null);
        setPhotoBase64(null);
        setPhotoMime(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    React.useEffect(() => {
        if (!user?.id) navigate("/login");
    }, [user?.id, navigate]);

    const createOrderAndCheckout = async () => {
        const validationError = validateStoryForm({ childName, theme, lesson, addr });
        if (validationError) {
            toast.error(validationError);
            return;
        }
        if (!dedicationChoice) {
            toast.error("Please choose a dedication option — you can skip it if you like.");
            return;
        }
        setSubmitting(true);
        try {
            const { data: order } = await api.post("/orders", {
                child_name: childName,
                book_title: bookTitle,
                theme, lesson, style,
                shipping_address: addr,
                child_photo_base64: photoBase64,
                child_photo_mime: photoMime,
                attire_mode: attireMode,
                dedication_choice: dedicationChoice,
                dedication_text: dedicationChoice === "skip" ? null : (dedicationText.trim() || null),
            });
            const { data: ck } = await api.post("/checkout/session", {
                order_id: order.id,
                origin_url: window.location.origin,
            });
            // Stripe checkout sets X-Frame-Options: DENY, so we cannot navigate
            // the preview's iframe to checkout.stripe.com. Open in a new tab.
            const popup = window.open(ck.url, "_blank");
            navigate(`/order/${order.id}?awaiting_payment=1&checkout_url=${encodeURIComponent(ck.url)}`);
            if (!popup) {
                toast.message("Popup blocked — use the 'Resume payment' link on the next page.");
            } else {
                toast.success("Opening secure Stripe checkout in a new tab…");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create order");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen paper-bg">
            <Navbar />
            <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl mx-auto px-6 md:px-10 pt-12 pb-24"
            >
                <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">A new book</p>
                <h1 className="font-serif text-4xl md:text-5xl text-primary tracking-tight mb-3">Tell us about the hero.</h1>
                <p className="text-muted-foreground mb-12 leading-relaxed">We&apos;ll write a 24-page story, illustrate every page, and mail you a hardcover.</p>

                <div className="space-y-7">
                    <Field label="Child's name">
                        <Input
                            data-testid={SF.formChildName}
                            value={childName}
                            onChange={(e) => setChildName(e.target.value)}
                            placeholder="e.g., Maya"
                            className="h-12 bg-muted/40 border-border"
                        />
                    </Field>

                    <Field
                        label="Book title (optional)"
                        hint={(() => {
                            const themeTitle = (theme || "adventure").split(" ").map(w => w ? w[0].toUpperCase() + w.slice(1) : "").join(" ");
                            const name = childName || "your child";
                            const possessive = name.endsWith("s") ? `${name}'` : `${name}'s`;
                            return `Leave blank and we'll title it "${possessive} ${themeTitle} Adventure"`;
                        })()}
                    >
                        <Input
                            data-testid="sf-form-book-title"
                            value={bookTitle}
                            onChange={(e) => setBookTitle(e.target.value)}
                            placeholder={childName && theme ? `${childName}'s ${theme.split(" ").map(w => w ? w[0].toUpperCase() + w.slice(1) : "").join(" ")} Adventure` : "e.g., Maya's Whispering Forest Adventure"}
                            maxLength={80}
                            className="h-12 bg-muted/40 border-border"
                        />
                    </Field>

                    <Field label="Story theme" hint="The world or adventure they'll go on">
                        <Input
                            data-testid={SF.formTheme}
                            value={theme}
                            onChange={(e) => setTheme(e.target.value)}
                            placeholder="e.g., a deep-sea adventure with talking sea creatures"
                            className="h-12 bg-muted/40 border-border"
                        />
                    </Field>

                    <Field label="Lesson to teach" hint="The heart of the story">
                        <Input
                            data-testid={SF.formLesson}
                            value={lesson}
                            onChange={(e) => setLesson(e.target.value)}
                            placeholder="e.g., it's brave to ask for help"
                            className="h-12 bg-muted/40 border-border"
                        />
                    </Field>

                    <Field label="Illustration style">
                        <Select value={style} onValueChange={setStyle}>
                            <SelectTrigger data-testid={SF.formStyle} className="h-12 bg-muted/40 border-border">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STYLES.map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>

                    <Field label="A photo of your child (optional)" hint="We&apos;ll use it as a likeness reference so the illustrated character resembles them.">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/heic"
                            onChange={onPhotoChange}
                            className="hidden"
                            data-testid="sf-form-photo-input"
                        />
                        {photoPreview ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-4 paper-card rounded-2xl p-3">
                                    <img src={photoPreview} alt="child preview" className="w-20 h-20 rounded-xl object-cover" data-testid="sf-form-photo-preview" />
                                    <div className="flex-1">
                                        <p className="text-sm text-foreground">Photo ready</p>
                                        <p className="text-xs text-muted-foreground">Used only to generate a likeness description.</p>
                                    </div>
                                    <Button type="button" variant="ghost" size="icon" onClick={clearPhoto} data-testid="sf-form-photo-clear" aria-label="Remove photo">
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                                <div className="paper-card rounded-2xl p-4" data-testid="sf-form-attire-toggle">
                                    <p className="text-sm text-foreground mb-3">What should they wear in the illustrations?</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setAttireMode("generated")}
                                            data-testid="sf-form-attire-generated"
                                            className={`text-left px-4 py-3 rounded-xl border transition-colors ${attireMode === "generated" ? "border-primary bg-primary/5" : "border-border bg-muted/30 hover:bg-muted/50"}`}
                                        >
                                            <p className="text-sm text-foreground">Dress them for the adventure</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">An outfit matched to your story&apos;s theme</p>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setAttireMode("photo")}
                                            data-testid="sf-form-attire-photo"
                                            className={`text-left px-4 py-3 rounded-xl border transition-colors ${attireMode === "photo" ? "border-primary bg-primary/5" : "border-border bg-muted/30 hover:bg-muted/50"}`}
                                        >
                                            <p className="text-sm text-foreground">Use their actual outfit</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Exactly what they&apos;re wearing in the photo</p>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                data-testid="sf-form-photo-upload"
                                className="w-full flex items-center gap-3 h-14 px-5 rounded-xl border border-dashed border-border bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                            >
                                <Camera className="w-5 h-5 text-primary" strokeWidth={1.5} />
                                <div>
                                    <p className="text-sm text-foreground">Upload a photo of {childName || "your child"}</p>
                                    <p className="text-xs text-muted-foreground">JPG or PNG, up to 6 MB</p>
                                </div>
                            </button>
                        )}
                    </Field>

                    <DedicationSection
                        childName={childName}
                        choice={dedicationChoice}
                        setChoice={setDedicationChoice}
                        text={dedicationText}
                        setText={setDedicationText}
                    />

                    <div className="pt-6 mt-6 border-t border-border">
                        <h2 className="font-serif text-2xl text-primary mb-5">Ship the hardcover to</h2>
                        <ShippingAddressFields addr={addr} onChange={setAddr} />
                    </div>

                    <div className="pt-8">
                        <Button
                            onClick={createOrderAndCheckout}
                            disabled={submitting}
                            data-testid={SF.formCheckout}
                            className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base"
                        >
                            {submitting ? "Working…" : "Continue to checkout — $45"}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center pt-2">Payment is secured by Stripe. Sequels remember your character.</p>
                </div>
            </motion.div>
        </div>
    );
}

function Field({ label, hint, children, full }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <Label className="text-sm">{label}</Label>
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
            <div className="mt-2">{children}</div>
        </div>
    );
}
